import { useMemo, useState } from 'react';
import { ImagePlus, Loader2, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { MapleMarketApp } from '@/data/mapleMarketApps';

interface AssetsTabProps {
  product: MapleMarketApp;
  disabled: boolean;
  onSaved: () => Promise<void> | void;
}

function getFileExtension(file: File) {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension) {
    return extension;
  }

  if (file.type === 'image/png') {
    return 'png';
  }

  if (file.type === 'image/webp') {
    return 'webp';
  }

  return 'jpg';
}

export default function AssetsTab({ product, disabled, onSaved }: AssetsTabProps) {
  const { toast } = useToast();
  const [heroFile, setHeroFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const previewUrls = useMemo(
    () => ({
      hero: heroFile ? URL.createObjectURL(heroFile) : product.heroImageUrl || product.image,
      thumbnail: thumbnailFile ? URL.createObjectURL(thumbnailFile) : product.thumbnailUrl || product.image,
    }),
    [heroFile, product.heroImageUrl, product.image, product.thumbnailUrl, thumbnailFile]
  );

  const handleUpload = async () => {
    if (!heroFile && !thumbnailFile) {
      toast({
        title: 'No assets selected',
        description: 'Choose at least one image before uploading.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    const updates: Record<string, string> = {};
    const storage = supabase.storage.from('marketplace');

    try {
      if (heroFile) {
        const extension = getFileExtension(heroFile);
        const path = `products/${product.sku}/hero.${extension}`;
        const { error } = await storage.upload(path, heroFile, {
          upsert: true,
          contentType: heroFile.type || 'image/jpeg',
        });

        if (error) {
          throw error;
        }

        updates.hero_image_url = storage.getPublicUrl(path).data.publicUrl;
      }

      if (thumbnailFile) {
        const extension = getFileExtension(thumbnailFile);
        const path = `products/${product.sku}/thumbnail.${extension}`;
        const { error } = await storage.upload(path, thumbnailFile, {
          upsert: true,
          contentType: thumbnailFile.type || 'image/jpeg',
        });

        if (error) {
          throw error;
        }

        updates.thumbnail_url = storage.getPublicUrl(path).data.publicUrl;
      }

      const { error: updateError } = await (supabase.from('marketplace_products') as any)
        .update(updates)
        .eq('id', product.id);

      if (updateError) {
        throw updateError;
      }

      setHeroFile(null);
      setThumbnailFile(null);
      await onSaved();
      toast({
        title: 'Assets updated',
        description: 'Marketplace imagery has been uploaded successfully.',
      });
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Unable to upload marketplace assets.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Assets</h3>
          <p className="text-sm text-muted-foreground">Upload hero and thumbnail images to the public marketplace storage bucket.</p>
        </div>
        <Button type="button" onClick={() => void handleUpload()} disabled={disabled || saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Upload selected assets
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Hero image</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="overflow-hidden rounded-xl border border-border bg-muted/30">
              <img src={previewUrls.hero} alt={`${product.name} hero`} className="aspect-video w-full object-cover" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="market-hero-upload">Choose hero image</Label>
              <Input
                id="market-hero-upload"
                type="file"
                accept="image/*"
                disabled={disabled}
                onChange={(event) => setHeroFile(event.target.files?.[0] ?? null)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Thumbnail</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="overflow-hidden rounded-xl border border-border bg-muted/30">
              <img src={previewUrls.thumbnail} alt={`${product.name} thumbnail`} className="aspect-video w-full object-cover" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="market-thumbnail-upload">Choose thumbnail image</Label>
              <Input
                id="market-thumbnail-upload"
                type="file"
                accept="image/*"
                disabled={disabled}
                onChange={(event) => setThumbnailFile(event.target.files?.[0] ?? null)}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {!disabled && (
        <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-4 text-sm text-muted-foreground">
          <div className="mb-1 flex items-center gap-2 font-medium text-foreground">
            <ImagePlus className="h-4 w-4 text-primary" />
            Storage path convention
          </div>
          Assets are uploaded to <code>products/{product.sku}/hero.*</code> and <code>products/{product.sku}/thumbnail.*</code> in the public <code>marketplace</code> bucket.
        </div>
      )}
    </div>
  );
}
